import {
  buildCancelOutboundPayload,
  buildChatOutboundPayload,
  parseInboundJson,
  randomRequestId,
} from './chatbotWsFrames';

/**
 * Long-lived chatbot WebSocket (lazy connect on first {@link #sendTurn}).
 *
 * @param {() => Promise<string>} getConnectUrl — resolved URL (e.g. with query chatId)
 */
export class ChatbotWebSocketSession {
  constructor(getConnectUrl) {
    this.getConnectUrl = getConnectUrl;
    /** @type {WebSocket | null} */
    this.ws = null;
    /** @type {Promise<void> | null} */
    this._opening = null;
    /** @type {string} */
    this.lastConversationId = '';
    /** @type {object | null} */
    this.activeTurn = null;
    this.closedByUser = false;
    /** Bumped on {@link #close} to abandon in-flight connect attempts. */
    this._connectEpoch = 0;
  }

  /**
   * @param {string} url
   * @param {number} epoch
   * @returns {Promise<void>}
   */
  _openSocket(url, epoch) {
    return new Promise((resolve, reject) => {
      if (this.closedByUser || epoch !== this._connectEpoch) {
        reject(new Error('Connection aborted'));
        return;
      }

      const staleSocket = this.ws;
      if (staleSocket && staleSocket.readyState !== WebSocket.CLOSED) {
        try {
          staleSocket.removeEventListener('message', this._onMessage);
          staleSocket.removeEventListener('close', this._onClose);
          staleSocket.close();
        } catch {
          /* ignore */
        }
        this.ws = null;
      }

      let socket;
      let opened = false;
      try {
        socket = new WebSocket(url);
      } catch (err) {
        reject(err);
        return;
      }
      this.ws = socket;

      const onOpen = () => {
        if (opened || epoch !== this._connectEpoch || this.closedByUser) {
          try {
            socket.close();
          } catch {
            /* ignore */
          }
          if (this.ws === socket) {
            this.ws = null;
          }
          if (!opened) {
            reject(new Error('Connection aborted'));
          }
          return;
        }
        opened = true;
        socket.removeEventListener('open', onOpen);
        socket.removeEventListener('error', onError);
        socket.removeEventListener('close', onClose);
        socket.addEventListener('message', this._onMessage);
        socket.addEventListener('close', this._onClose);
        resolve();
      };

      const fail = (err) => {
        if (opened) {
          return;
        }
        socket.removeEventListener('open', onOpen);
        socket.removeEventListener('error', onError);
        socket.removeEventListener('close', onClose);
        if (this.ws === socket) {
          this.ws = null;
        }
        try {
          socket.close();
        } catch {
          /* ignore */
        }
        reject(err);
      };

      const onError = () => {
        fail(new Error('WebSocket error'));
      };

      const onClose = (ev) => {
        if (!opened) {
          const reason =
            (ev && typeof ev.reason === 'string' && ev.reason.trim()) ||
            `WebSocket closed (${ev?.code ?? 'unknown'})`;
          fail(new Error(reason));
        }
      };

      socket.addEventListener('open', onOpen, { once: true });
      socket.addEventListener('error', onError, { once: true });
      socket.addEventListener('close', onClose, { once: true });
    });
  }

  /**
   * @returns {Promise<void>}
   */
  async ensureOpen() {
    if (this.closedByUser) {
      throw new Error('Connection closed');
    }
    if (this.ws?.readyState === WebSocket.OPEN) {
      return undefined;
    }
    if (this._opening) {
      return this._opening;
    }

    const epoch = this._connectEpoch;
    const openAttempt = (async () => {
      const url = await this.getConnectUrl();
      if (this.closedByUser || epoch !== this._connectEpoch) {
        throw new Error('Connection aborted');
      }
      if (!url) {
        throw new Error('Chatbot WebSocket URL is not configured.');
      }
      if (this.ws?.readyState === WebSocket.OPEN) {
        return undefined;
      }
      await this._openSocket(url, epoch);
    })();

    this._opening = openAttempt;
    try {
      await openAttempt;
    } finally {
      if (this._opening === openAttempt) {
        this._opening = null;
      }
    }
    return undefined;
  }

  _onMessage = (ev) => {
    const frame = parseInboundJson(ev.data);
    if (!frame) {
      return;
    }
    switch (frame.type) {
      case 'ready':
      case 'pong':
        return;
      case 'conversation': {
        if (typeof frame.conversationId === 'string' && frame.conversationId) {
          this.lastConversationId = frame.conversationId;
          if (this.activeTurn && !this.activeTurn.settled) {
            this.activeTurn.onConversationId?.(frame.conversationId);
          }
        }
        return;
      }
      case 'message': {
        if (
          !this.activeTurn ||
          this.activeTurn.settled ||
          frame.requestId !== this.activeTurn.requestId ||
          typeof frame.content !== 'string'
        ) {
          return;
        }
        this.activeTurn.assistantText += frame.content;
        if (frame.content.length > 0) {
          this.activeTurn.onTextDelta?.(frame.content);
        }
        return;
      }
      case 'error': {
        const msg = frame.message || 'Unknown error';
        if (
          this.activeTurn &&
          !this.activeTurn.settled &&
          (!frame.requestId || frame.requestId === this.activeTurn.requestId)
        ) {
          this._rejectActiveTurn(new Error(msg));
        }
        return;
      }
      case 'done': {
        if (
          !this.activeTurn ||
          this.activeTurn.settled ||
          frame.requestId !== this.activeTurn.requestId
        ) {
          return;
        }
        this._resolveActiveTurn();
        break;
      }
      default: {
        break;
      }
    }
  };

  _onClose = () => {
    const socket = this.ws;
    if (socket) {
      socket.removeEventListener('message', this._onMessage);
      socket.removeEventListener('close', this._onClose);
    }
    this.ws = null;
    if (this.activeTurn && !this.activeTurn.settled) {
      this._rejectActiveTurn(new Error('Connection closed'));
    }
  };

  _resolveActiveTurn() {
    const t = this.activeTurn;
    if (!t || t.settled) {
      return;
    }
    t.settled = true;
    this.activeTurn = null;
    const conv = this.lastConversationId || t.initialConversationId || '';
    t.resolve({
      assistantText: t.assistantText,
      conversationId: conv,
    });
  }

  _rejectActiveTurn(err) {
    const t = this.activeTurn;
    if (!t || t.settled) {
      return;
    }
    t.settled = true;
    this.activeTurn = null;
    t.reject(err);
  }

  /**
   * Same contract as {@link ../api/postChatbotSse.postChatbotSse}.
   *
   * @param {{
   *   message: string,
   *   conversationId?: string,
   *   settings: object,
   *   onTextDelta?: (chunk: string) => void,
   *   onConversationId?: (id: string) => void,
   * }} params
   * @returns {Promise<{ assistantText: string, conversationId: string }>}
   */
  sendTurn(params) {
    const {
      message,
      conversationId = '',
      settings,
      onTextDelta,
      onConversationId,
    } = params;

    return this.ensureOpen().then(() => {
      const ws = this.ws;
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        throw new Error('WebSocket is not connected.');
      }

      const trimmed = (message || '').trim();
      if (!trimmed) {
        return Promise.resolve({
          assistantText: '',
          conversationId: this.lastConversationId || conversationId || '',
        });
      }

      const requestId = randomRequestId();
      const initialConversationId =
        typeof conversationId === 'string' ? conversationId : '';

      return new Promise((resolve, reject) => {
        this.activeTurn = {
          requestId,
          assistantText: '',
          initialConversationId,
          settled: false,
          onTextDelta,
          onConversationId,
          resolve,
          reject,
        };

        try {
          ws.send(
            buildChatOutboundPayload({
              requestId,
              message: trimmed,
              ...(initialConversationId
                ? { conversationId: initialConversationId }
                : {}),
              settings,
            }),
          );
        } catch (e) {
          this.activeTurn = null;
          reject(e);
        }
      });
    });
  }

  /**
   * @param {string} requestId
   */
  sendCancel(requestId) {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN || !requestId) {
      return;
    }
    try {
      ws.send(buildCancelOutboundPayload(requestId));
    } catch {
      /* ignore */
    }
  }

  close() {
    this.closedByUser = true;
    this._connectEpoch += 1;
    this._opening = null;
    if (this.activeTurn && !this.activeTurn.settled) {
      this._rejectActiveTurn(new Error('Connection closed'));
    }
    const socket = this.ws;
    this.ws = null;
    if (socket) {
      socket.removeEventListener('message', this._onMessage);
      socket.removeEventListener('close', this._onClose);
      if (socket.readyState !== WebSocket.CLOSED) {
        try {
          socket.close();
        } catch {
          /* ignore */
        }
      }
    }
  }
}
